import { GetStaticPaths, GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import type { BlogAuthorFragment, BlogPostFragment } from 'types';
import type { BlogCategory } from '@root/sanity/sanity.types';
import { BLOG_AUTHOR_POSTS_PER_PAGE } from '@root/constants/blog';
import { PAGE_BLOG, PAGE_BLOG_AUTHOR } from '@root/constants/routes';
import { getAllBlogAuthors, getBlogAuthorBySlug } from '@root/server/services/Blog';
import BlogList from 'components/Blog/List';
import BlogPagination from 'components/Blog/Pagination';
import BlogPicture from 'components/Blog/Picture';
import { PortableText } from 'components/sanity/serializers';
import LayoutBlog from 'layout/Blog';
import { getAllBlogPosts, getAllBlogCategories } from 'services/blog';
import { withPageStaticProps } from 'utils/header';
import { getParameterByName } from 'utils/utils';

export const getTotalPages = (totalPosts: number) => Math.ceil(totalPosts / BLOG_AUTHOR_POSTS_PER_PAGE);

type BlogAuthorPageProps = {
  blogAuthor: BlogAuthorFragment;
  blogAuthorPosts: BlogPostFragment[];
  blogCategories: BlogCategory[];
};

const BlogAuthorPage = ({ blogAuthor, blogAuthorPosts, blogCategories }: BlogAuthorPageProps) => {
  const [page, setPage] = useState(1);
  const router = useRouter();
  const totalPosts = blogAuthorPosts.length;

  useEffect(() => {
    const qsPage = getParameterByName('page') ? Number(getParameterByName('page')) : page;

    if (qsPage && qsPage !== page) {
      setPage(qsPage);
    }
  }, []);

  const getRenderedPosts = () => {
    const offsetStart = (page - 1) * BLOG_AUTHOR_POSTS_PER_PAGE;
    const offsetEnd = offsetStart + BLOG_AUTHOR_POSTS_PER_PAGE;

    return blogAuthorPosts.slice(offsetStart, offsetEnd);
  };

  const handleUpdatePage = ({ page }: { page: number }) => {
    router.push({
      pathname: `${PAGE_BLOG}/author/${blogAuthor.slug.current}`,
      query: {
        page,
      },
    });

    setPage(page);
  };

  const totalPages = getTotalPages(totalPosts);
  const renderedPosts = getRenderedPosts();

  return (
    <>
      <Head>
        <title>{blogAuthor.name ?? 'Generation Tux Author'}</title>
      </Head>
      <LayoutBlog categories={blogCategories} pathname={PAGE_BLOG_AUTHOR} showLogo={false}>
        <section className="container">
          <div className="row my-16 grid-cols-12 pb-16 sm:my-128">
            <div className="col-span-12 xs:col-span-8 xs:col-start-3 md:col-span-4 md:col-start-2 md:self-center">
              <BlogPicture
                alt={`${blogAuthor.picture.alt} thumbnail`}
                className="col-span-1 aspect-ratio-1by1 md:col-span-8 md:aspect-ratio-5by7"
                imgClassName="block h-full w-full object-contain md:object-cover"
                nativeWidth={blogAuthor.picture.asset.metadata?.dimensions?.width!}
                slot="indexCard"
                url={blogAuthor.picture.asset.url!}
              />
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-7 md:col-end-12">
              <h2 className="text-h1-display mt-16 mb-16 sm:mt-0">{blogAuthor.name}</h2>
              <PortableText content={blogAuthor.longBioRaw as [object]} />
            </div>
          </div>
          {renderedPosts?.length > 0 && (
            <>
              <h2 className="text-h2 mb-16">{`Blogs by ${blogAuthor.name}`}</h2>
              <BlogList posts={renderedPosts} />
              <BlogPagination activeBlogCategory="author" page={page} totalPages={totalPages} updatePage={handleUpdatePage} />
            </>
          )}
        </section>
      </LayoutBlog>
    </>
  );
};

// This function gets called at build time
// { fallback: false } means other routes should 404.
export const getStaticPaths: GetStaticPaths = async () => {
  const blogAuthors = await getAllBlogAuthors();

  return {
    paths: blogAuthors.map(({ slug }) => ({
      params: {
        slug: slug.current,
      },
    })),
    fallback: false,
  };
};

export const getStaticProps = withPageStaticProps(async ({ params }: GetStaticPropsContext) => {
  try {
    const { slug } = params!;
    const blogAuthor = await getBlogAuthorBySlug(String(slug));
    const blogCategories = await getAllBlogCategories();
    const allBlogPosts = await getAllBlogPosts();
    const blogAuthorPosts = allBlogPosts.filter((post) => post.author.slug.current === blogAuthor?.slug.current);

    return {
      props: {
        blogAuthor,
        blogAuthorPosts,
        blogCategories,
      },
    };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get blog author');
  }
});

export default BlogAuthorPage;
